import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, Save, MessageSquarePlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { welcomeApi } from '@/api/welcome.api';

export const Route = createFileRoute('/_authenticated/bienvenida')({
  component: BienvenidaPage,
});

const MAX_LENGTH = 500;

function BienvenidaPage() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const { data: welcome, isLoading } = useQuery({
    queryKey: ['welcome'],
    queryFn: welcomeApi.get,
  });

  useEffect(() => {
    if (welcome) {
      setText(welcome.text ?? '');
      setImageUrl(welcome.imageUrl ?? null);
    }
  }, [welcome]);

  const uploadMutation = useMutation({
    mutationFn: welcomeApi.uploadImage,
    onSuccess: (data) => {
      setImageUrl(data.imageUrl);
      toast.success('Imagen subida');
    },
    onError: () => toast.error('Error al subir la imagen'),
  });

  const saveMutation = useMutation({
    mutationFn: welcomeApi.update,
    onSuccess: () => {
      toast.success('Bienvenida guardada');
      qc.invalidateQueries({ queryKey: ['welcome'] });
    },
    onError: () => toast.error('Error al guardar'),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate(file);
    e.target.value = '';
  };

  const handleSave = () => {
    saveMutation.mutate({ text, imageUrl: imageUrl ?? undefined });
  };

  if (isLoading) {
    return (
      <>
        <TopBanner title="Mensaje de bienvenida" subtitle="Lo que ve el jugador apenas entra al chat" />
        <div className="flex-1 overflow-auto p-4 lg:p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  return (
    <>
      <TopBanner title="Mensaje de bienvenida" subtitle="Lo que ve el jugador apenas entra al chat" />
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="grid gap-6 max-w-4xl lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquarePlus className="h-5 w-5" />
                Configuración
              </CardTitle>
              <CardDescription>Texto e imagen que se muestran al entrar al chat</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="welcome-text" className="text-sm font-medium leading-none">
                  Texto de bienvenida
                </label>
                <textarea
                  id="welcome-text"
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, MAX_LENGTH))}
                  maxLength={MAX_LENGTH}
                  rows={5}
                  placeholder="¡Bienvenido! Escribinos y te cargamos las fichas 🎰"
                  className={cn(
                    'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none',
                  )}
                />
                <div className="text-right text-xs text-muted-foreground">
                  {text.length}/{MAX_LENGTH}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium leading-none">Imagen de bienvenida</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 lg:h-10"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadMutation.isPending}
                  >
                    {uploadMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="mr-2 h-4 w-4" />
                    )}
                    {imageUrl ? 'Cambiar imagen' : 'Subir imagen'}
                  </Button>
                </div>
                {imageUrl && (
                  <div className="mt-2 overflow-hidden rounded-md border border-input">
                    <img src={imageUrl} alt="Bienvenida" className="max-h-64 w-full object-contain" />
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSave} disabled={saveMutation.isPending} size="lg">
                  {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  Guardar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Vista previa</CardTitle>
              <CardDescription>Así lo va a ver el jugador en el chat</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-input bg-muted/30 p-4">
                <div className="flex max-w-[80%] flex-col gap-2 rounded-2xl rounded-tl-sm bg-card p-3 shadow-sm">
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="Preview bienvenida"
                      className="max-h-48 w-full rounded-lg object-cover"
                    />
                  )}
                  <p className="whitespace-pre-wrap text-sm">
                    {text || 'Escribí tu mensaje de bienvenida para ver la vista previa acá.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
